import { AddIcon } from '@chakra-ui/icons'
import { Box, Button, Center, Grid, Text, VStack } from '@chakra-ui/react'
import { useAlertDialogDispatcher } from 'context/ui/alertDialog'
import { ShowModalParams } from 'context/ui/modal'
import { CreateMySpotInput, TopMySpotItemFragment } from 'generated/graphql'
import { FC, useRef, useState } from 'react'
import { buildConfirmDeleteMySpotModal } from './confirmDeleteMySpotModal'
import { TopMySpotForm } from './topMySpotForm'
import { useTopMySpotMutations } from './useTopMySpotMutations'

const MAX_MY_SPOTS = 20

type Props = {
  mySpots: TopMySpotItemFragment[]
  refetch: () => Promise<unknown>
}

type EditableRow = {
  key: string
  spot?: TopMySpotItemFragment
}

export const buildEditMySpotsModal = (props: Props): ShowModalParams => ({
  size: '6xl',
  header: 'マイスポット編集',
  hideCloseButton: true,
  body: <EditMySpotsModalBody {...props} />,
})

export const EditMySpotsModalBody: FC<Props> = ({ mySpots, refetch }) => {
  const { showAlertDialog } = useAlertDialogDispatcher()
  const draftSequence = useRef(mySpots.length === 0 ? 1 : 0)
  const [rows, setRows] = useState<EditableRow[]>(mySpots.length > 0 ? mySpots.map(spot => ({ key: spot.id, spot })) : [{ key: 'draft-0' }])
  const [submittingRowKey, setSubmittingRowKey] = useState<string>()
  const { create, update, remove } = useTopMySpotMutations(refetch)

  const createSpot = async (rowKey: string, input: CreateMySpotInput) => {
    setSubmittingRowKey(rowKey)
    try {
      const savedSpot = await create(input)
      if (savedSpot) {
        setRows(current => current.map(row => (row.key === rowKey ? { key: savedSpot.id, spot: savedSpot } : row)))
      }
      return savedSpot
    } finally {
      setSubmittingRowKey(undefined)
    }
  }

  const updateSpot = async (spot: TopMySpotItemFragment, input: CreateMySpotInput) => {
    setSubmittingRowKey(spot.id)
    try {
      const savedSpot = await update(spot.id, input)
      if (savedSpot) {
        setRows(current => current.map(row => (row.key === spot.id ? { key: savedSpot.id, spot: savedSpot } : row)))
      }
      return savedSpot
    } finally {
      setSubmittingRowKey(undefined)
    }
  }

  const deleteSpot = async (spot: Pick<TopMySpotItemFragment, 'id' | 'title'>): Promise<boolean> => {
    const succeeded = await remove(spot)
    if (succeeded) setRows(current => current.filter(row => row.key !== spot.id))
    return succeeded
  }

  const addDraft = () => {
    const key = `draft-${draftSequence.current}`
    draftSequence.current += 1
    setRows(current => [...current, { key }])
  }

  const removeRow = (row: EditableRow) => {
    if (!row.spot) {
      setRows(current => current.filter(currentRow => currentRow.key !== row.key))
      return
    }
    showAlertDialog(buildConfirmDeleteMySpotModal({ spot: row.spot, onConfirm: deleteSpot }))
  }

  const reachedLimit = rows.length >= MAX_MY_SPOTS

  return (
    <VStack maxH="calc(100vh - 96px)" spacing={0} align="stretch">
      <Box flex={1} minH={0} px="32px" pt="32px" overflowX="auto" overflowY="auto">
        {rows.length === 0 ? (
          <Center py="40px" textAlign="center">
            <Text>
              登録されているスポットはありません。
              <br />
              「新しいスポットを追加する」からスポットを追加してください。
            </Text>
          </Center>
        ) : (
          <Box minW="1016px" borderWidth="1px" borderColor="gray.100">
            <Grid
              templateColumns="minmax(220px, 1.1fr) minmax(260px, 1.2fr) 110px 170px 156px"
              bg="blue.50"
              fontWeight="bold"
              textAlign="center"
            >
              <Text p="16px 12px">スポットタイトル</Text>
              <Text p="16px 12px">Google Map URL / 緯度・経度</Text>
              <Text p="16px 12px">登録日</Text>
              <Text p="16px 12px">登録者</Text>
              <Text p="16px 12px">操作</Text>
            </Grid>
            {rows.map(row => (
              <TopMySpotForm
                key={row.key}
                spot={row.spot}
                isSubmitting={submittingRowKey === row.key}
                onSubmit={input => (row.spot ? updateSpot(row.spot, input) : createSpot(row.key, input))}
                onDelete={() => removeRow(row)}
              />
            ))}
          </Box>
        )}
      </Box>

      <Center mt="32px" px="32px" py="24px" borderTopWidth="1px" borderColor="gray.200" flexShrink={0}>
        <VStack spacing="8px">
          <Button leftIcon={<AddIcon />} minW="300px" colorScheme="blue" isDisabled={reachedLimit} onClick={addDraft}>
            新しいスポットを追加する
          </Button>
          {reachedLimit && (
            <Text color="red.500" fontSize="sm">
              マイスポットは20件まで登録できます
            </Text>
          )}
        </VStack>
      </Center>
    </VStack>
  )
}
